import { useState, useRef, useCallback } from 'react';
import type { KeyboardEvent, ChangeEvent } from 'react';
import { cn } from '@/utils/cn';

export interface TagInputProps {
  /** Field label displayed above the input */
  label: string;
  /** Current array of tag strings */
  tags: string[];
  /** Callback when tags change */
  onChange: (tags: string[]) => void;
  /** Input placeholder text */
  placeholder?: string;
  /** Maximum number of tags allowed */
  maxTags?: number;
  /** Predefined suggestions shown as clickable chips below the input */
  suggestions?: string[];
  /** Helper text shown below the input */
  helpText?: string;
  /** Disable the input */
  disabled?: boolean;
}

export default function TagInput({
  label,
  tags,
  onChange,
  placeholder = 'Type and press Enter',
  maxTags,
  suggestions,
  helpText,
  disabled = false,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const atLimit = maxTags !== undefined && tags.length >= maxTags;

  const addTag = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      if (atLimit) return;
      // Deduplicate (case-insensitive)
      if (tags.some((t) => t.toLowerCase() === trimmed.toLowerCase())) return;
      onChange([...tags, trimmed]);
      setInputValue('');
    },
    [tags, onChange, atLimit]
  );

  const removeTag = useCallback(
    (index: number) => {
      onChange(tags.filter((_, i) => i !== index));
    },
    [tags, onChange]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        addTag(inputValue);
      } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
        removeTag(tags.length - 1);
      }
    },
    [inputValue, addTag, removeTag, tags.length]
  );

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // If they pasted something with commas, split and add each
    if (val.includes(',')) {
      const parts = val.split(',');
      parts.forEach((part) => addTag(part));
      return;
    }
    setInputValue(val);
  }, [addTag]);

  // Filter suggestions to only show ones not already added
  const availableSuggestions = suggestions?.filter(
    (s) => !tags.some((t) => t.toLowerCase() === s.toLowerCase())
  );

  // Focus the input when clicking the container
  const handleContainerClick = useCallback(() => {
    if (!disabled) inputRef.current?.focus();
  }, [disabled]);

  return (
    <div className="w-full space-y-1.5">
      {/* Label */}
      <label className="block text-sm font-medium text-text-secondary">{label}</label>

      {/* Tag container + input */}
      <div
        onClick={handleContainerClick}
        className={cn(
          'flex flex-wrap items-center gap-1.5 min-h-[2.5rem] w-full rounded-[10px] border bg-dark-elevated-2 px-2 py-1.5 text-sm cursor-text transition-colors',
          isFocused
            ? 'border-accent ring-1 ring-accent/30'
            : 'border-border-subtle',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        {/* Rendered tags */}
        {tags.map((tag, index) => (
          <span
            key={`${tag}-${index}`}
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-2.5 py-0.5 text-xs font-medium transition-colors',
              'bg-accent/15 text-accent border border-accent/25'
            )}
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(index);
                }}
                className="ml-0.5 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-accent/60 hover:text-accent hover:bg-accent/20 transition-colors"
                aria-label={`Remove ${tag}`}
              >
                <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </span>
        ))}

        {/* Input field */}
        {!atLimit && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              // Commit any pending input on blur
              if (inputValue.trim()) addTag(inputValue);
            }}
            placeholder={tags.length === 0 ? placeholder : ''}
            disabled={disabled}
            className="flex-1 min-w-[120px] border-0 bg-transparent p-0.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-0"
          />
        )}
      </div>

      {/* Limit indicator */}
      {maxTags !== undefined && (
        <p className={cn(
          'text-xs',
          atLimit ? 'text-warning' : 'text-text-muted'
        )}>
          {tags.length}/{maxTags} {atLimit ? '(limit reached)' : ''}
        </p>
      )}

      {/* Suggestions */}
      {availableSuggestions && availableSuggestions.length > 0 && !atLimit && (
        <div className="space-y-1">
          <p className="text-xs text-text-muted">Suggestions:</p>
          <div className="flex flex-wrap gap-1.5">
            {availableSuggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                disabled={disabled}
                onClick={() => addTag(suggestion)}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium border transition-all',
                  'bg-dark-elevated-1 text-text-muted border-border-subtle',
                  'hover:bg-accent/10 hover:text-accent hover:border-accent/30',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Help text */}
      {helpText && (
        <p className="text-xs text-text-muted">{helpText}</p>
      )}
    </div>
  );
}
